import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { SubscriptionStore } from '../stores/subscription.store';
import { TenantContextService } from './tenant-context.service';
import { AuthService } from './auth.service';

type ShownKind = 'grace' | 'expired';

/**
 * Decides whether the post-login status modal should be shown.
 *
 * Grace → one-shot per session per BU (dismissal persisted in sessionStorage).
 * Expired → mandatory on every session start; suppressed once the user picks
 * an action (Continue / Request Exemption / Logout) until next login.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionStatusModalService {
  private store   = inject(SubscriptionStore);
  private tenant  = inject(TenantContextService);
  private auth    = inject(AuthService);

  private static readonly KEY_PREFIX = 'subStatusModalShown:';

  private _visibleKind = signal<ShownKind | null>(null);
  readonly visibleKind = this._visibleKind.asReadonly();

  readonly currentPeriodEnd = computed(() => this.store.current()?.currentPeriodEnd ?? null);
  readonly graceEndsAt      = computed(() => this.store.current()?.gracePeriodEndsAt ?? null);
  readonly planName         = computed(() => this.store.planName());

  constructor() {
    effect(() => {
      if (this.auth.isSuperAdminSignal()) {
        this._visibleKind.set(null);
        return;
      }
      const sub = this.store.current();
      const buId = this.tenant.selectedBusinessUnitId();
      if (!sub || !buId) {
        this._visibleKind.set(null);
        return;
      }
      if (sub.status === 'Expired') {
        if (!this.alreadyShownThisSession(buId, 'expired')) {
          this._visibleKind.set('expired');
        }
      } else if (sub.status === 'Grace') {
        if (!this.alreadyShownThisSession(buId, 'grace')) {
          this._visibleKind.set('grace');
        }
      } else {
        this._visibleKind.set(null);
      }
    });
  }

  dismiss(): void {
    const kind = this._visibleKind();
    const buId = this.tenant.getCurrentBusinessUnitId();
    if (kind && buId) this.markShown(buId, kind);
    this._visibleKind.set(null);
  }

  /** Called when login flow completes — clears any per-session "already shown" flags so the modal can fire again. */
  resetForNewSession(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(SubscriptionStatusModalService.KEY_PREFIX)) keysToRemove.push(k);
      }
      keysToRemove.forEach(k => sessionStorage.removeItem(k));
    } catch {
      // sessionStorage unavailable (private mode etc.) — nothing to clear
    }
  }

  private alreadyShownThisSession(buId: string, kind: ShownKind): boolean {
    try {
      return sessionStorage.getItem(this.key(buId, kind)) === '1';
    } catch {
      return false;
    }
  }

  private markShown(buId: string, kind: ShownKind): void {
    try {
      sessionStorage.setItem(this.key(buId, kind), '1');
    } catch {
      // ignore
    }
  }

  private key(buId: string, kind: ShownKind): string {
    return `${SubscriptionStatusModalService.KEY_PREFIX}${buId}:${kind}`;
  }
}
